import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../../services/api';
import testService from '../../services/testService';
import { filterPublished } from '../../utils/publishedFilter';
import { 
  BOARDS_EXAMS, 
  ACADEMIC_YEARS, 
  SUBJECTS, 
  SUBJECT_TOPICS, 
  ASSIGNMENTS, 
  STUDENT_ATTEMPTS, 
  TESTS
} from '../../config/Config';
import {
  fetchUnifiedTeacherStudents,
  getManagedStudentRecordId,
  getPlatformStudentPk,
} from '../../utils/teacherStudentRoster';

/*
  Props:
    open         : boolean
    student      : { id, email }
    onClose      : (changed:boolean)=>void
*/

const defaultFilters = { board: '', year: '', subject: '', topic: '', difficulty: '' };
const allFilterOption = { id: '', label: 'All' };
const difficultyOptions = [
  allFilterOption,
  { id: '10', label: '<=10%' },
  { id: '10+', label: '>10%' },
  { id: '20+', label: '>20%' },
  { id: '40+', label: '>40%' },
  { id: '60+', label: '>60%' },
  { id: '80+', label: '>80%' },
];

function AssignTestsDialog({ open, student: initialStudent, onClose }) {
  const [tests, setTests] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [student, setStudent] = useState(initialStudent || null);
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [years, setYears] = useState([]);
  const [topics, setTopics] = useState([]);
  
  const boardMap = useMemo(() => Object.fromEntries(boards.map(b => [Number(b.id), b.name || b.label || b])), [boards]);
  const subjectMap = useMemo(() => Object.fromEntries(subjects.map(s => [Number(s.id), s.name || s.title || s.label || s.subject || s] )), [subjects]);
  const yearMap = useMemo(() => Object.fromEntries(years.map(y => [Number(y.id), y.label || y.year || y.name || y])), [years]);
  const topicMap = useMemo(() => Object.fromEntries(topics.map(t => [Number(t.id), t.name || t.topic || t.title || t])), [topics]);

  const resolveBoard = test => {
    const id = Number(
      test.created_for_board ?? test.created_for_board_id ??
      test.board ?? test.board_id ?? (test.board && test.board.id)
    );
    const nameCandidate =
      test.created_for_board_name ||
      test.board_name ||
      (test.created_for_board && test.created_for_board.name) ||
      (test.board && typeof test.board === 'object' && test.board.name) ||
      (typeof test.board === 'string' ? test.board : undefined);
    return boardMap[id] || nameCandidate || id || '';
  };
  const resolveSubject = test => {
    const id = Number(test.subject ?? test.subject_id ?? test.subject?.id);
    return test.subject_name || subjectMap[id] || test.subject?.name || test.subject_label || id || '';
  };
  const resolveYear = test => {
    const id = Number(test.academic_year ?? test.year ?? test.academic_year_id ?? test.academic_year?.id);
    return yearMap[id] || test.academic_year_label || test.year_label || test.academic_year_name || id || '';
  };
  const resolveTopic = test => {
    if (test.topic || test.topic_id) {
      const id = Number(test.topic ?? test.topic_id);
      return topicMap[id] || test.topic_name || id || '';
    }
    if (Array.isArray(test.topics)) {
      return test.topics.map(id => topicMap[Number(id)] || id).join(', ');
    }
    return '';
  };

  const [filters, setFilters] = useState(defaultFilters);
  const pickId = (...values) => {
    for (const value of values) {
      if (value == null || value === '') continue;
      if (typeof value === 'object') {
        const nested = value.id ?? value.pk ?? value.value;
        if (nested != null && nested !== '') return String(nested);
        continue;
      }
      return String(value);
    }
    return '';
  };
  const getBoardId = t => pickId(t.created_for_board, t.created_for_board_id, t.board, t.board_id);
  const getYearId  = t => pickId(t.academic_year, t.year, t.academic_year_id);
  const getSubjectId = t => pickId(t.subject, t.subject_id);
  const normalizeFilterLabel = value => String(value || '').trim().toLowerCase();
  const matchesIdOrLabel = (filterValue, rowId, selectedLabel, rowLabel) => {
    if (!filterValue) return true;
    if (rowId && String(rowId) === String(filterValue)) return true;
    const selected = normalizeFilterLabel(selectedLabel);
    const row = normalizeFilterLabel(rowLabel);
    return Boolean(selected && row && selected === row);
  };
  const getTopicIds = t => {
    const ids = [];
    const addTopic = (topic) => {
      const id = pickId(topic, topic?.id, topic?.pk, topic?.value, topic?.topic_id);
      if (id) ids.push(id);
    };
    addTopic(t.topic ?? t.topic_id);
    if (Array.isArray(t.topics)) t.topics.forEach(addTopic);
    return ids;
  };
  const getTopicLabels = t => {
    const labels = [];
    if (t.topic_name) labels.push(t.topic_name);
    if (t.topic && typeof t.topic === 'object') {
      labels.push(t.topic.name || t.topic.title || t.topic.topic || '');
    }
    if (Array.isArray(t.topics_list)) labels.push(...t.topics_list);
    if (Array.isArray(t.topics)) {
      t.topics.forEach((topic) => {
        if (topic && typeof topic === 'object') {
          labels.push(topic.name || topic.title || topic.topic || '');
        } else {
          labels.push(topicMap[Number(topic)] || '');
        }
      });
    }
    return labels.map(normalizeFilterLabel).filter(Boolean);
  };

  const testRows = useMemo(() => tests.map(t => ({
    ...t,
    id: t.id ?? t.pk ?? Math.random()
  })), [tests]);

  const visibleRows = useMemo(()=>{
    return testRows.filter(t=>{
      if (!matchesIdOrLabel(filters.board, getBoardId(t), boardMap[Number(filters.board)], resolveBoard(t))) return false;
      if (!matchesIdOrLabel(filters.year, getYearId(t), yearMap[Number(filters.year)], resolveYear(t))) return false;
      if (!matchesIdOrLabel(filters.subject, getSubjectId(t), subjectMap[Number(filters.subject)], resolveSubject(t))) return false;
      if (filters.topic) {
        const topicIds = getTopicIds(t);
        const rowTopicLabels = getTopicLabels(t);
        const selectedTopicLabel = normalizeFilterLabel(topicMap[Number(filters.topic)]);
        if (
          !topicIds.some(id => String(id) === String(filters.topic)) &&
          !(selectedTopicLabel && rowTopicLabels.includes(selectedTopicLabel))
        ) {
          return false;
        }
      }
      
      if(filters.difficulty) {
        const totalAdvanced = (t.percent_advanced || 0) + (t.percent_intermediate || 0);
        
        if(filters.difficulty === '10') {
          if(totalAdvanced > 10) return false;
        } else if(filters.difficulty === '10+') {
          if(totalAdvanced <= 10) return false;
        } else if(filters.difficulty === '20+') {
          if(totalAdvanced <= 20) return false;
        } else if(filters.difficulty === '40+') {
          if(totalAdvanced <= 40) return false;
        } else if(filters.difficulty === '60+') {
          if(totalAdvanced <= 60) return false;
        } else if(filters.difficulty === '80+') {
          if(totalAdvanced <= 80) return false;
        }
      }
      
      return true;
    });
  }, [testRows, filters, boardMap, yearMap, subjectMap, topicMap]);
  
  const [loading, setLoading] = useState(false);
  

  const filteredTopics = useMemo(() => {
    if (!filters.subject) return topics;
    const subjId = Number(filters.subject);
    return topics.filter(t => {
      const subjField = t.subject ?? t.subject_id ?? (t.subject && t.subject.id);
      return Number(subjField) === subjId;
    });
  }, [topics, filters.subject]);
  const [existingAssignments, setExistingAssignments] = useState({});
  const [selectionModel, setSelectionModel] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  useEffect(()=>{
    const assignedIds = Object.keys(existingAssignments).map(Number);
    setSelectionModel(prev=>{
      const merged = new Set([...prev, ...assignedIds]);
      visibleRows.forEach(r=>{});
      const final = Array.from(merged).filter(id=>visibleRows.some(r=>r.id===id));
      return final;
    });
  }, [visibleRows, existingAssignments]);
  const [targetDates, setTargetDates] = useState({});
  
  const [completedTestIds, setCompletedTestIds] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  const showMsg = (msg, severity = 'success') => setSnackbar({ open: true, msg, severity });

  const fetchStudents = async () => {
    try {
      setStudentsLoading(true);
      const list = await fetchUnifiedTeacherStudents(api);
      const activeStudents = list.filter((s) => s.is_active !== false);
      setStudents(activeStudents);
    } catch (err) {
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchLOVs = async () => {
    try {
      const [boardsRes, yearsRes, subjectsRes, topicsRes] = await Promise.all([
        api.get(BOARDS_EXAMS + '?page_size=100&published_only=true'),
        api.get(ACADEMIC_YEARS + '?page_size=100&published_only=true'),
        api.get(SUBJECTS + '?page_size=100'),
        api.get(SUBJECT_TOPICS + '?page_size=1000')
      ]);
      setBoards(filterPublished(Array.isArray(boardsRes.data) ? boardsRes.data : boardsRes.data.results || []));
      setYears(filterPublished(Array.isArray(yearsRes.data) ? yearsRes.data : yearsRes.data.results || []));
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : subjectsRes.data.results || []);
      setTopics(Array.isArray(topicsRes.data) ? topicsRes.data : topicsRes.data.results || []);
          } catch (err) {
    }
  };

  const fetchAssignments = async () => {
    if (!student) return Promise.resolve();

    const msId = getManagedStudentRecordId(student);
    const platformId = getPlatformStudentPk(student);
    if (!msId && !platformId) {
      return Promise.resolve();
    }

    try {
      const url = msId
        ? `${ASSIGNMENTS}?teacher_managed_student_id=${msId}`
        : `${ASSIGNMENTS}?student=${platformId}`;
      const res = await api.get(url);

      const itemsRaw = Array.isArray(res.data) ? res.data : res.data.results || [];
      const items = msId
        ? itemsRaw.filter((a) => Number(a.teacher_managed_student_id) === Number(msId))
        : itemsRaw;
      
      const map = {};
      items.forEach(a => (map[a.test] = a.id));
      setExistingAssignments(Object.fromEntries(items.map(a => [a.test, { id: a.id, target_date: a.target_date }])));
      setSelectionModel(Object.keys(map).map(Number));
      
      try {
        const params = msId
          ? { teacher_managed_student_id: msId, submitted: true, page_size: 1000 }
          : { student: platformId, submitted: true, page_size: 1000 };
        const attRes = await api.get(STUDENT_ATTEMPTS, { params });
        const attempts = Array.isArray(attRes.data)? attRes.data : attRes.data.results || [];
        setCompletedTestIds(attempts.map(a=>a.test));
      } catch(err){} 
    } catch (err) {
    }
  };

  const buildParams = () => {
      const p = { status: 'PUBLISHED', page_size: 1000, mine: 'false' };
      return p;
    };

  const fetchTests = async () => {
    setLoading(true);
    try {
      const res = await api.get(TESTS, { params: buildParams() });
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];
      setTests(list);
    } catch (err) {
      showMsg(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{
    if(open){
      setFilters(defaultFilters);
      setSelectionModel([]);
      setTargetDates({});
      setCompletedTestIds([]);
      setExistingAssignments({});
      setStudent(initialStudent || null);
    }
  },[open, initialStudent]);

  useEffect(() => {
    if (open) {
      fetchLOVs();
      if (student) {
        fetchAssignments().then(() => {
          fetchTests();
        });
      } else {
        setTests([]);
      }
    }
  }, [open, student]);

  useEffect(() => {
    if (open) fetchStudents();
  }, [open]);

  useEffect(() => {
    if (open && student) fetchTests();
  }, [filters]);

  const subjectGetter = (params) => {
    const r = params.row;
    const id = Number(r.subject ?? r.subject_id ?? (r.subject && r.subject.id));
    return subjectMap[id] || r.subject_name || (r.subject && r.subject.name) || '';
  };
  const yearGetter = (params) => {
    const r = params.row;
    const id = Number(r.academic_year ?? r.year ?? r.academic_year_id ?? (r.academic_year && r.academic_year.id));
    return yearMap[id] || r.academic_year_label || r.year_label || '';
  };
  const topicGetter = (params) => {
    const r = params.row;
    if (r.topic || r.topic_id || (r.topic && typeof r.topic === 'object')) {
      const topicObj = r.topic && typeof r.topic === 'object' ? r.topic : null;
      if (topicObj) return topicObj.name || topicObj.title || topicObj.topic || '';
      const id = Number(r.topic ?? r.topic_id);
      return topicMap[id] || r.topic_name || '';
    }
    if (Array.isArray(r.topics_list)) {
      return r.topics_list.join(', ');
    }
    if (Array.isArray(r.topics)) {
      return r.topics.map((t) => {
        if (typeof t === 'object') return t.name || t.title || t.topic || '';
        return topicMap[Number(t)] || t;
      }).join(', ');
    }
    return '';
  };

  const difficultyGetter = (params) => {
    const r = params.row;
    const basic = r.percent_basic || 0;
    const intermediate = r.percent_intermediate || 0;
    const advanced = r.percent_advanced || 0;
    
    if (basic === 0 && intermediate === 0 && advanced === 0) {
      return 'Not specified';
    }
    
    const parts = [];
    if (basic > 0) parts.push(`${basic}% Basic`);
    if (intermediate > 0) parts.push(`${intermediate}% Intermediate`);
    if (advanced > 0) parts.push(`${advanced}% Advanced`);
    
    return parts.join(', ');
  };

  const questionDurationGetter = (params) => {
    const r = params.row;
    const questionCount = r.question_count || r.questions_count || r.num_questions || 0;
    const duration = r.duration_minutes || r.duration || 0;
    
    const questionText = questionCount > 0 ? `${questionCount} Q` : '0 Q';
    const durationText = duration > 0 ? `${duration} min` : '0 min';
    
    return `${questionText} / ${durationText}`;
  };

  const testColumns = [
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'description', headerName: 'Test Description', width: 250, 
      valueGetter: (params) => {
        const description = params.row.description || 'No description available';
        return description.length > 50 ? `${description.substring(0, 50)}...` : description;
      },
      renderCell: (params) => {
        const description = params.row.description || 'No description available';
        const displayText = description.length > 50 ? `${description.substring(0, 50)}...` : description;
        
        return (
          <Tooltip 
            title={description} 
            placement="top"
            enterDelay={500}
            leaveDelay={0}
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  maxWidth: 300,
                  fontSize: '0.875rem',
                  padding: '8px 12px'
                }
              }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: 'help',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayText}
            </Typography>
          </Tooltip>
        );
      }
    },
    { field: 'question_duration', headerName: 'No of question/Duration', width: 150, valueGetter: questionDurationGetter },
    { field: 'board', headerName: 'Board', flex: 1, valueGetter: params=>resolveBoard(params.row) },
    { field: 'subject', headerName: 'Subject', width: 130, valueGetter: subjectGetter },
    { field: 'topic', headerName: 'Topic', width: 160, valueGetter: topicGetter },
    { field: 'academic_year', headerName: 'Year', width: 90, valueGetter: yearGetter },
    { field: 'difficulty', headerName: 'Difficulty', width: 200, valueGetter: difficultyGetter },
    {
      field: 'target_date', headerName: 'Target Date', width: 140, sortable: false,
      renderCell: (params) => {
        const id = params.row.id;
        const handleDateChange = (e) => {
          const newValue = e.target.value;
          setTargetDates(prev => ({ ...prev, [id]: newValue }));
        };
        
        return (
          <TextField
            type="date"
            size="small"
            value={targetDates[id] || existingAssignments[id]?.target_date || ''}
            onChange={handleDateChange}
            required
            error={selectionModel.includes(id) && !completedTestIds.some(cid => Number(cid) === Number(id)) && !(targetDates[id] || existingAssignments[id]?.target_date)}
            InputLabelProps={{ shrink: true }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        );
      }
    }
  ];

  const handleSave = async () => {
    const missingDateIds = selectionModel.filter(id => {
      if (completedTestIds.some(cid => Number(cid) === Number(id))) return false;
      const hasDate = targetDates[id] || existingAssignments[id]?.target_date;
      return !hasDate;
    });
    if (missingDateIds.length > 0) {
      showMsg('Please set a Target Date for every selected test (excluding completed tests).', 'error');
      return;
    }
    if (!student) return;
    
    const msId = getManagedStudentRecordId(student);
    const platformId = getPlatformStudentPk(student);
    if (!msId && !platformId) {
      return;
    }

    const newIds = selectionModel;
    const currentIds = Object.keys(existingAssignments).map(Number);
    const toAdd = newIds.filter(id => !currentIds.includes(id));
    const toRemove = currentIds.filter(id => !newIds.includes(id) && !completedTestIds.includes(id));
    if (toAdd.length === 0 && toRemove.length === 0) {
      showMsg('No changes made');
      return;
    }
    try {
      await Promise.all([
        ...toAdd.map(id =>
          api.post(ASSIGNMENTS, {
            test: id,
            ...(msId
              ? { teacher_managed_student_ids: [msId] }
              : { students: [platformId] }),
            target_date: targetDates[id] || tests.find(t => t.id === id)?.target_date || null,
          })
        ),
        ...Object.keys(existingAssignments)
          .filter(tid => selectionModel.includes(Number(tid)) && targetDates[tid])
          .map(tid => api.patch(
            `/exams/assignments/${existingAssignments[tid].id}/`, 
            { target_date: targetDates[tid] }
          )),
        ...toRemove.map(id => api.delete(`${ASSIGNMENTS}${existingAssignments[id].id}/`)),
      ]);
      showMsg('Assignments updated');
      onClose(true);
    } catch (err) {
      showMsg(err.message, 'error');
    }
  };

  const handleFilterAutocompleteChange = field => (_, option) => {
    const value = option?.id == null ? '' : String(option.id);
    setFilters(prev => {
      if (field === 'subject') {
        return { ...prev, subject: value, topic: '' };
      }
      return { ...prev, [field]: value };
    });
  };

  const findFilterOption = (options, value) =>
    options.find(option => String(option.id) === String(value)) || allFilterOption;

  const renderFilterAutocomplete = ({ field, label, options, minWidth = 160 }) => (
    <Autocomplete
      options={options}
      value={findFilterOption(options, filters[field])}
      onChange={handleFilterAutocompleteChange(field)}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
      size="small"
      clearOnEscape
      renderInput={(params) => (
        <TextField {...params} label={label} InputLabelProps={{ shrink: true }} />
      )}
      sx={{ minWidth }}
    />
  );

  const boardFilterOptions = [allFilterOption, ...boards.map(b => ({ id: b.id, label: b.name || b.label || '' }))];
  const yearFilterOptions = [allFilterOption, ...years.map(y => ({ id: y.id, label: String(y.label || y.year || '') }))];
  const subjectFilterOptions = [allFilterOption, ...subjects.map(s => ({ id: s.id, label: s.name || '' }))];
  const topicFilterOptions = [allFilterOption, ...filteredTopics.map(t => ({ id: t.id, label: t.name || t.topic || '' }))];

  return (
    <Dialog 
      open={open} 
      onClose={() => onClose(false)} 
      fullWidth 
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
          width: '95vw',
          maxWidth: '1400px'
        }
      }}
    >
      <DialogTitle>
        Assign Tests to {student ? (student.email || student.display_name || student.name || 'Student') : ''}
      </DialogTitle>
      <DialogContent sx={{ minHeight: '60vh' }}>
        <Stack direction="row" spacing={2} sx={{ mb:2 }}>
          <Autocomplete
            options={students}
            getOptionLabel={(opt) => (opt.email || opt.display_name || opt.name || '').trim() || 'Student'}
            loading={studentsLoading}
            value={student}
            onChange={(e,val)=>setStudent(val)}
            renderInput={(params)=>(<TextField {...params} label="Student" placeholder="Select student" size="small" />)}
            sx={{ minWidth: 260 }}
          />
        </Stack>
        <Stack direction="row" spacing={2} mb={2} useFlexGap flexWrap="wrap">
          {renderFilterAutocomplete({ field: 'board', label: 'Board', options: boardFilterOptions, minWidth: 180 })}
          {renderFilterAutocomplete({ field: 'year', label: 'Year', options: yearFilterOptions, minWidth: 130 })}
          {renderFilterAutocomplete({ field: 'subject', label: 'Subject', options: subjectFilterOptions, minWidth: 190 })}
          {renderFilterAutocomplete({ field: 'topic', label: 'Topic', options: topicFilterOptions, minWidth: 230 })}
          {renderFilterAutocomplete({ field: 'difficulty', label: 'Difficulty', options: difficultyOptions, minWidth: 180 })}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Legend:</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <div style={{ width: 16, height: 16, backgroundColor: 'transparent', border: '1px solid #ccc' }}></div>
            <Typography variant="caption">Available</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <div style={{ width: 16, height: 16, backgroundColor: 'success.light', opacity: 0.7 }}></div>
            <Typography variant="caption">Already Assigned</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <div style={{ width: 16, height: 16, backgroundColor: 'action.disabledBackground' }}></div>
            <Typography variant="caption">Completed</Typography>
          </Stack>
        </Stack>
        {loading ? (
          <CircularProgress />
        ) : (
          <DataGrid
            autoHeight
            columns={testColumns}
            rows={visibleRows}
            loading={loading}
            checkboxSelection
            rowsPerPageOptions={[10, 25, 50]}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            getRowId={row => row.id}
            selectionModel={selectionModel}
            onSelectionModelChange={setSelectionModel}
            isRowSelectable={(params)=>{
              const testId = params.id;
              const numericTestId = Number(testId);
              const isCompleted = completedTestIds.some(id => Number(id) === numericTestId);
              const isAssigned = Object.keys(existingAssignments).some(id => Number(id) === numericTestId);
              
              if (isCompleted) return false;
              if (isAssigned) return false;
              return true;
            }}
            getRowClassName={(params)=>{
              const testId = Number(params.id);
              const isCompleted = completedTestIds.some(id => Number(id) === testId);
              const isAssigned = Object.keys(existingAssignments).some(id => Number(id) === testId);
              
              if (isCompleted) return 'completed-row';
              if (isAssigned) return 'assigned-row';
              return '';
            }}
            sx={{
              '& .completed-row': {
                bgcolor: 'action.disabledBackground',
                '&:hover': { bgcolor: 'action.disabledBackground' }
              },
              '& .assigned-row': {
                bgcolor: 'success.light',
                opacity: 0.7,
                '&:hover': { bgcolor: 'success.light' }
              },
              minHeight: '400px'
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!student}>Assign</Button>
      </DialogActions>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.msg}</Alert>
      </Snackbar>
    </Dialog>
  );
}

export default AssignTestsDialog;
